import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { environment } from '../../environments/environment.js';
import {
  Coinflow3DSCheckoutError,
  Coinflow3DSOptions,
  CoinflowCardPaymentForm
} from '../components/payments/common.js';
import { CoinflowService } from './coinflow.js';

export interface CoinflowChargebackProtectionItem {
  productName: string;
  productType: string;
  quantity: number | string;
  rawProductData?: object;
}

export interface CoinflowCheckoutResponse {
  _3dsError?: Coinflow3DSCheckoutError;
  paymentId?: string;
}

export interface CoinflowCustomerInfo {
  firstName: string;
  lastName: string;
}

export function coinflowCustomerInfo(
  user: { first_name?: string | null; last_name?: string | null } | null | undefined
): CoinflowCustomerInfo | undefined {
  const firstName = user?.first_name?.trim();
  const lastName = user?.last_name?.trim();
  return firstName && lastName ? { firstName, lastName } : undefined;
}

export interface CoinflowChargeParams {
  merchantId: string;
  sessionKey: string;
  subtotalCents: number;
  /**
   * Opaque passthrough — the client never inspects or merges it. Typed `object`
   * (not Record<string, unknown>) so interface-typed payloads (TrackingParams,
   * MerchCharge['webhookInfo']) assign without index-signature friction.
   */
  webhookInfo: object;
  /**
   * Optional because Coinflow assesses a buyer-paid fee whenever the payload
   * carries it — gaming passes its own data deliberately; merch omits it.
   * JSON serialization drops the key when undefined.
   */
  chargebackProtectionData?: readonly CoinflowChargebackProtectionItem[];
  /**
   * The account holder's profile name, never the card form's (that travels as
   * `card.firstName`/`card.lastName`). Coinflow compares the cardholder name
   * against it pre-authorization and declines a mismatch with code 873.
   */
  customerInfo?: CoinflowCustomerInfo;
}

/**
 * Mechanical Coinflow checkout client: POST the charge, set the two auth
 * headers, map HTTP 412 to the 3DS challenge shape. It composes nothing —
 * no environment merchant, no session-key knowledge, no chargeback defaults.
 * Merchant/session/amount/webhook composition must stay in the flow services
 * (PurchaseService, MerchCoinflowService); a merchant id must never be paired
 * with a session key minted for a different one.
 */
@Injectable({ providedIn: 'root' })
export class CoinflowCheckoutService {
  private readonly _http = inject(HttpClient);
  private readonly _coinflow = inject(CoinflowService);

  /** POST {coinflow.apiUrl}/checkout/card/{merchantId} — body key `card`. */
  async checkoutNewCard(
    charge: CoinflowChargeParams,
    card: CoinflowCardPaymentForm,
    authentication3DS: Coinflow3DSOptions
  ): Promise<CoinflowCheckoutResponse> {
    return this._checkout(`/checkout/card/${charge.merchantId}`, { card }, charge, authentication3DS);
  }

  /** POST {coinflow.apiUrl}/checkout/token/{merchantId} — body key `token`. */
  async checkoutSavedCard(
    charge: CoinflowChargeParams,
    cardToken: string,
    authentication3DS: Coinflow3DSOptions
  ): Promise<CoinflowCheckoutResponse> {
    return this._checkout(`/checkout/token/${charge.merchantId}`, { token: cardToken }, charge, authentication3DS);
  }

  private _checkout(
    path: string,
    payment: { card: CoinflowCardPaymentForm } | { token: string },
    charge: CoinflowChargeParams,
    authentication3DS: Coinflow3DSOptions
  ): Promise<CoinflowCheckoutResponse> {
    const resp$ = this._http
      .post<CoinflowCheckoutResponse>(
        `${environment.coinflow.apiUrl}${path}`,
        {
          authentication3DS,
          ...payment,
          subtotal: { cents: charge.subtotalCents },
          chargebackProtectionData: charge.chargebackProtectionData,
          webhookInfo: charge.webhookInfo,
          ...(charge.customerInfo ? { customerInfo: charge.customerInfo } : {}),
          // Hardcoded because both flows send exactly this today (saveCard
          // accrues vault cards even where no picker exists yet); parameterize
          // only when a flow actually differs.
          saveCard: true,
          authOnly: true
        },
        {
          headers: {
            'x-coinflow-auth-session-key': charge.sessionKey,
            'x-device-id': this._coinflow.deviceId
          }
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 412) {
            const { transactionId, creq, url } = err.error;
            return of({ _3dsError: { transactionId, creq, url } as Coinflow3DSCheckoutError });
          }
          throw err;
        })
      );

    return firstValueFrom(resp$);
  }
}
